import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button, Tooltip } from '@mui/material';
import plusIcon from '../../assets/icons/plus.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import eyeIcon from '../../assets/icons/eye.svg';
import rapprochementAPI from '../../services/rapprochementAPI';
import { caisseAPI } from '../../services/api';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import Rows from '../../components/ui/Rows';
import CaisseSelector from '../../components/ui/CaisseSelector';
import CircularIconButton from '../../components/ui/CircularIconButton';
import RapprochementFilters from './RapprochementFilters';

const HEADER_HEIGHT = 76;

const contentContainerSx = {
  width: '100%',
  margin: '0 auto',
  paddingLeft: { xs: '26px', sm: '42px' },
  paddingRight: { xs: '26px', sm: '42px' },
};

const Rapprochements = () => {
  const history = useHistory();
  const location = useLocation();

  const [rapprochements, setRapprochements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [caisses, setCaisses] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: 20, total: 0 });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('DESC');

  const [filters, setFilters] = useState(() => {
    const q = new URLSearchParams(location.search);
    return {
      selectedCaisseIds: q.get('caisseIds') ? q.get('caisseIds').split(',').map(Number) : [],
      searchTerm: q.get('search') || '',
      createdByIds: q.get('createdByIds') ? q.get('createdByIds').split(',').map(Number) : [],
      dateRange:
        q.get('fromDate') && q.get('toDate')
          ? { start: new Date(q.get('fromDate')), end: new Date(q.get('toDate')) }
          : null,
    };
  });

  useEffect(() => {
    caisseAPI.getForFilters(false)
      .then((res) => {
        const mobileCaisses = (res.data || []).filter(c => c.type === 'MOBILE');
        setCaisses(mobileCaisses);
        // If no caisses are selected via URL, default to all
        if (filters.selectedCaisseIds.length === 0) {
          const allCaisseIds = mobileCaisses.map(c => c.id);
          setFilters(prev => ({ ...prev, selectedCaisseIds: allCaisseIds }));
        }
      })
      .catch((err) => console.error('Error loading caisses:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRapprochements = useCallback(async () => {
    if (filters.selectedCaisseIds.length === 0) {
      setRapprochements([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy,
        sortDirection,
      };
      if (filters.selectedCaisseIds.length === 1) params.caisseId = filters.selectedCaisseIds[0];
      if (filters.searchTerm) params.searchTerm = filters.searchTerm;
      if (filters.createdByIds?.length > 0) params.createdById = filters.createdByIds[0];
      if (filters.dateRange?.start)
        params.fromDate = filters.dateRange.start.toLocaleDateString('en-CA');
      if (filters.dateRange?.end)
        params.toDate = filters.dateRange.end.toLocaleDateString('en-CA');

      const res = await rapprochementAPI.getAll(params);
      setRapprochements(res.data.content || []);
      setPagination((prev) => ({ ...prev, total: res.data.totalElements || 0 }));
    } catch (err) {
      console.error('Error fetching rapprochements:', err);
      setError('Échec du chargement des rapprochements');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.size, sortBy, sortDirection]);

  useEffect(() => {
    fetchRapprochements();
  }, [fetchRapprochements]);

  const writeToUrl = (f) => {
    const params = new URLSearchParams();
    if (f.selectedCaisseIds?.length) params.set('caisseIds', f.selectedCaisseIds.join(','));
    if (f.searchTerm) params.set('search', f.searchTerm);
    if (f.createdByIds?.length) params.set('createdByIds', f.createdByIds.join(','));
    if (f.dateRange?.start) params.set('fromDate', f.dateRange.start.toLocaleDateString('en-CA'));
    if (f.dateRange?.end) params.set('toDate', f.dateRange.end.toLocaleDateString('en-CA'));
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const handleReset = () => {
    const empty = { selectedCaisseIds: [], searchTerm: '', createdByIds: [], dateRange: null };
    history.replace({ pathname: location.pathname, search: '' });
    setFilters(empty);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (updated) => {
    const next = { ...filters, ...updated };
    setFilters(next);
    writeToUrl(next);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} - ${hours}:${minutes}`;
  };

  const renderEcart = (ecart) => {
    if (ecart == null) return null;
    const num = parseFloat(ecart);
    let color = '#919191';
    if (num > 0) color = '#0AD69F';
    else if (num < 0) color = '#EF456E';
    return (
      <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19px', color }}>
        {formatAmountForDisplay(num, false).text}
      </span>
    );
  };

  const columns = [
    {
      key: 'versementCode',
      label: 'Versement ID',
      sortable: true,
      render: (v) => (
        <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, color: '#38485C' }}>
          {v}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Date',
      sortable: true,
      render: (v) => formatDate(v),
    },
    {
      key: 'caisseName',
      label: 'Caisse',
      sortable: true,
      render: (v) => (
        <span style={{
          fontFamily: 'Montserrat',
          fontSize: '16px',
          fontWeight: 600,
          color: '#38485C',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          overflow: 'hidden',
          wordBreak: 'break-word',
          lineHeight: '1.2',
        }}>
          {v}
        </span>
      ),
    },
    {
      key: 'createdBy',
      label: 'Par',
      sortable: true,
      render: (_, row) => {
        const cb = row.originalData.createdBy;
        return cb ? `${cb.firstName} ${cb.lastName}`.trim() : '';
      },
    },
    {
      key: 'versementAmount',
      label: 'Versement',
      sortable: true,
      render: (v) => (
        <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19px', color: '#38485C' }}>
          {formatAmountForDisplay(v, false).text}
        </span>
      ),
    },
    {
      key: 'transactionsNet',
      label: 'Transactions',
      sortable: false,
      render: (v) => (
        <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19px', color: '#38485C' }}>
          {formatAmountForDisplay(v, false).text}
        </span>
      ),
    },
    {
      key: 'verificationsTotal',
      label: 'Vérification',
      sortable: false,
      render: (v) => (
        <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19px', color: '#38485C' }}>
          {formatAmountForDisplay(v, false).text}
        </span>
      ),
    },
    {
      key: 'ecart',
      label: 'Écart',
      sortable: false,
      render: (v) => renderEcart(v),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: '60px',
      render: (_, row) => (
        <Tooltip title="Voir Détails" arrow placement="top">
          <span>
            <CircularIconButton
              icon={eyeIcon}
              alt="Voir détails"
              tone="highlighted"
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/rapprochements/${row.originalData.id}`);
              }}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  const transformedData = rapprochements.map((r) => ({
    id: r.id,
    versementCode: r.versementCode || '',
    createdAt: r.createdAt,
    caisseName: r.caisseName || '',
    versementAmount: r.versementAmount,
    transactionsNet: r.transactionsNet,
    verificationsTotal: r.verificationsTotal,
    ecart: r.ecart,
    originalData: r,
  }));

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          zIndex: 999,
          background: 'linear-gradient(to bottom, rgba(255,255,255,1), rgba(248,248,248,1), #F8F8F8)',
        }}
      >
        <Box sx={{ ...contentContainerSx, paddingTop: '24px', paddingBottom: '8px' }}>
          {/* Title + Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h1
              style={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '32px',
                color: '#38485C',
                opacity: 0.88,
                margin: 0,
              }}
            >
              Rapprochements
            </h1>
            <Button
              variant="contained"
              startIcon={<img src={plusIcon} alt="plus" style={{ width: '16px', height: '16px' }} />}
              onClick={() => history.push('/rapprochements/nouveau')}
              sx={{
                height: '40px',
                backgroundColor: '#027BC6',
                boxShadow: '0px 20px 80px #00000011',
                borderRadius: '12px',
                whiteSpace: 'nowrap',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#025a9c' },
              }}
            >
              Créer un rapprochement
            </Button>
          </Box>

          {/* Caisse selector */}
          <Box sx={{ marginBottom: '12px' }}>
            <CaisseSelector
              caisses={caisses}
              selectedIds={filters.selectedCaisseIds}
              onChange={(ids) => {
                const next = { ...filters, selectedCaisseIds: ids };
                setFilters(next);
                writeToUrl(next);
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              hideTabs
            />
          </Box>

          {/* Filter row: Search + Par + Date */}
          <Box sx={{ marginBottom: '8px', position: 'relative', zIndex: 1001 }}>
            <RapprochementFilters
              filters={{
                searchTerm: filters.searchTerm,
                createdByIds: filters.createdByIds,
                dateRange: filters.dateRange,
              }}
              onFiltersChange={handleFilterChange}
              onReset={handleReset}
            />
          </Box>
        </Box>

        {/* Column headers */}
        <Box sx={contentContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '16px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat',
            }}
          >
            {columns.map((col, idx) => {
              const isActions = col.key === 'actions';
              const isSorted = sortBy === col.key;
              return (
                <Box
                  key={idx}
                  sx={{
                    paddingLeft: '24px',
                    ...(isActions
                      ? { flex: 'none', width: '60px', textAlign: 'center' }
                      : { flex: 1, minWidth: '150px' }),
                  }}
                >
                  {col.sortable ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        color: isSorted ? '#027BC6' : '#919191',
                        '&:hover': { color: '#027BC6' },
                        transition: 'color 0.2s',
                      }}
                      onClick={() =>
                        handleSort(col.key, sortBy === col.key && sortDirection === 'ASC' ? 'DESC' : 'ASC')
                      }
                    >
                      <span>{col.label}</span>
                      {isSorted && (
                        <img
                          src={chevronUpIcon}
                          alt=""
                          style={{
                            width: '12px',
                            height: '12px',
                            transform: sortDirection === 'DESC' ? 'rotate(180deg)' : 'none',
                            filter:
                              'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)',
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <span>{col.label}</span>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={contentContainerSx}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Rows
            columns={columns}
            data={transformedData}
            pagination={{ page: pagination.page, size: pagination.size, total: pagination.total }}
            onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
            onRowsPerPageChange={(s) => setPagination((prev) => ({ ...prev, size: s, page: 0 }))}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            wrapLayout={false}
            expandable={false}
            hideHeader={true}
            containerPadding={0}
          />
        )}
      </Box>
    </Box>
  );
};

export default Rapprochements;
